import React, { useMemo } from "react"
import {
  MentorGroupSubscriptionFragment,
  SchoolGroupSubscriptionFragment,
  UserMentorGroupSubscriptionMembershipFragment,
  UserMentorGroupSubscriptionsDocument,
  UserMentorGroupSubscriptionsQuery
} from "@pathwright/graphql-client/src/generated"
import { getIsSubscriptionUsable } from "../subscription/utils"
import useAuthAwareQuery from "../utils/useAuthAwareQuery"

export function useUserCommunityContext() {
  const communityQuery = useAuthAwareQuery<UserMentorGroupSubscriptionsQuery>(
    UserMentorGroupSubscriptionsDocument
  )

  return useMemo(() => {
    const { me } = communityQuery.data || {}
    const memberships =
      me?.mentorGroupMemberships?.edges?.map((e) => e?.node!) || []
    const communityGroups: MentorGroupSubscriptionFragment[] = []
    const communityGroupMemberships: UserMentorGroupSubscriptionMembershipFragment[] =
      []
    const communityGroupSubscriptions: SchoolGroupSubscriptionFragment[] = []
    const activeCommunityGroupSubscriptions: SchoolGroupSubscriptionFragment[] =
      []
    const communityGroupsWithActiveSubscription: MentorGroupSubscriptionFragment[] =
      []
    const inactiveCommunityGroupSubscriptions: SchoolGroupSubscriptionFragment[] =
      []
    const communityGroupsWithInactiveSubscription: MentorGroupSubscriptionFragment[] =
      []
    let isCommunityGroupOwner = false
    let isCommunityGroupAdmin = false
    if (memberships.length) {
      memberships.map((membership) => {
        const communityGroupSub = membership.group.group_subscription
        if (membership.role > 5) {
          isCommunityGroupAdmin = true
        }
        if (membership.group.owner?.id === me?.id) {
          isCommunityGroupOwner = true
        }
        communityGroupMemberships.push(membership)
        communityGroups.push(membership.group)

        if (communityGroupSub) {
          communityGroupSubscriptions.push(communityGroupSub)
          if (
            getIsSubscriptionUsable(communityGroupSub.subscription) &&
            !membership.access_revoked_time &&
            (membership.group?.seats_available || 0) >= 0
          ) {
            activeCommunityGroupSubscriptions.push(communityGroupSub)
            communityGroupsWithActiveSubscription.push(membership.group)
          } else {
            inactiveCommunityGroupSubscriptions.push(communityGroupSub)
            communityGroupsWithInactiveSubscription.push(membership.group)
          }
        }
      })
    }

    // Find if the user has an active membership for the provided group subscription.
    const hasActiveMembership = (group?: MentorGroupSubscriptionFragment) => {
      if (!group) return false

      return communityGroupMemberships.some((membership) => {
        return (
          membership.group.id === group.id && !membership.access_revoked_time
        )
      })
    }

    const reactivateCommunityGroupWithInactiveSubscription =
      communityGroupsWithInactiveSubscription.find(
        (cg) => cg.permissions?.can_edit_subscription
      )

    return {
      communityQuery,
      communityGroups,
      isCommunityGroupOwner,
      isCommunityGroupAdmin,
      communityGroupMemberships,
      communityGroupSubscriptions,
      activeCommunityGroupSubscriptions,
      activeCommunityGroupSubscription: activeCommunityGroupSubscriptions[0],
      communityGroupsWithActiveSubscription,
      communityGroupWithActiveSubscription:
        communityGroupsWithActiveSubscription[0],
      inactiveCommunityGroupSubscriptions,
      inactiveCommunityGroupSubscription:
        inactiveCommunityGroupSubscriptions[0],
      communityGroupsWithInactiveSubscription,
      communityGroupWithInactiveSubscription:
        communityGroupsWithInactiveSubscription[0],
      reactivateCommunityGroupWithInactiveSubscription,
      hasActiveMembership
    }
  }, [communityQuery.data])
}

export const withUserCommunity = (Component: React.FC) => (props: any) => {
  const communityProps = useUserCommunityContext()
  return <Component {...communityProps} {...props} />
}
